import { getSupportProduct } from "./supportProduct";

/**
 * Journal du widget de support IA (migration 20260916160000).
 *
 * Tout est best-effort : une erreur réseau ou une migration pas encore
 * appliquée ne doit jamais gêner la conversation. Les échanges sont stockés
 * côté serveur dans le schéma `private` (purge 90 j) et lus par
 * /admin/support-bot.
 */

const CONVERSATION_KEY = "doodates:support-chat-conversation-id";
const VISITOR_KEY = "doodates:support-visitor-id";

function readOrCreate(storage: Storage | undefined, key: string): string {
  const fresh = crypto.randomUUID();
  if (!storage) return fresh;
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    storage.setItem(key, fresh);
  } catch {
    // mode privé / quota — identifiant volatil
  }
  return fresh;
}

function safeStorage(kind: "local" | "session"): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined;
  }
}

async function call(fn: string, args: Record<string, unknown>) {
  try {
    // Import différé, comme le reste du widget : ne charge pas le client
    // Supabase tant qu'aucun message n'est envoyé.
    const { supabase } = await import("../lib/supabase");
    const { error } = await supabase.rpc(fn, args);
    if (error) console.warn(`[support-chat-log] ${fn} failed:`, error.message);
  } catch (err) {
    console.warn(`[support-chat-log] ${fn} threw:`, err);
  }
}

export type SupportChatOutcome =
  | "ticket_proposed"
  | "ticket_created"
  | "roadmap_proposed"
  | "roadmap_clicked";

export const supportChatLog = {
  getConversationId(): string {
    return readOrCreate(safeStorage("session"), CONVERSATION_KEY);
  },

  /** Nouvelle conversation (bouton « + » du widget). */
  resetConversation(): void {
    try {
      safeStorage("session")?.removeItem(CONVERSATION_KEY);
    } catch {
      // ignore
    }
  },

  logTurn(params: {
    userMessageId: string;
    userMessage: string;
    assistantMessageId: string;
    assistantMessage: string;
    isError: boolean;
    locale?: string;
  }): Promise<void> {
    return call("log_support_chat_turn", {
      p_conversation_id: this.getConversationId(),
      p_user_message_id: params.userMessageId,
      p_user_message: params.userMessage,
      p_assistant_message_id: params.assistantMessageId,
      p_assistant_message: params.assistantMessage,
      p_is_error: params.isError,
      p_visitor_id: readOrCreate(safeStorage("local"), VISITOR_KEY),
      p_product: getSupportProduct(),
      p_locale: params.locale ?? null,
      p_page: typeof window !== "undefined" ? window.location.pathname : null,
    });
  },

  classify(params: {
    messageId: string;
    classification?: string | null;
    action?: string | null;
    confidence?: number | null;
  }): Promise<void> {
    return call("classify_support_chat_message", {
      p_conversation_id: this.getConversationId(),
      p_message_id: params.messageId,
      p_classification: params.classification ?? null,
      p_suggested_action: params.action ?? null,
      p_confidence: params.confidence ?? null,
    });
  },

  markOutcome(outcome: SupportChatOutcome, ticketId?: string): Promise<void> {
    return call("mark_support_chat_outcome", {
      p_conversation_id: this.getConversationId(),
      p_outcome: outcome,
      p_ticket_id: ticketId ?? null,
    });
  },

  rate(messageId: string, rating: 1 | -1 | null): Promise<void> {
    return call("rate_support_chat_message", {
      p_conversation_id: this.getConversationId(),
      p_message_id: messageId,
      p_rating: rating,
    });
  },
};
